import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebase';
import { AIAskMessage } from '../types';

export interface AIAskSettings {
  usePersonalKey: boolean;
  apiKey: string;
  token: string;
  model: string;
  baseUrl: string;
}

interface AskAIPayload {
  prompt: string;
  history: Array<Pick<AIAskMessage, 'role' | 'content'>>;
  apiKey?: string;
  token?: string;
  model?: string;
  baseUrl?: string;
}

interface AskAIResult {
  answer: string;
  model: string;
}

interface AskAIOptions {
  usePersonalKey?: boolean;
  apiKey?: string;
  token?: string;
  model?: string;
  baseUrl?: string;
}

export const DEFAULT_AI_ASK_SETTINGS: AIAskSettings = {
  usePersonalKey: false,
  apiKey: '',
  token: '',
  model: 'gpt-4o',
  baseUrl: '',
};

const askAICallable = httpsCallable<AskAIPayload, AskAIResult>(functions, 'askAI');

function readErrorField(error: unknown, field: 'code' | 'message'): string {
  if (typeof error !== 'object' || error === null || !(field in error)) {
    return '';
  }

  const value = (error as Record<'code' | 'message', unknown>)[field];
  return typeof value === 'string' ? value : '';
}

function normalizeBaseUrl(value: string): string {
  let normalized = value.trim();
  if (!normalized) {
    return '';
  }

  normalized = normalized.replace(/\/+$/, '');
  normalized = normalized.replace(/\/chat\/completions$/i, '');
  normalized = normalized.replace(/\/responses$/i, '');
  normalized = normalized.replace(/\/models$/i, '');

  return normalized;
}

function normalizeSettings(settings: Partial<AIAskSettings> | null | undefined): AIAskSettings {
  return {
    usePersonalKey: Boolean(settings?.usePersonalKey),
    apiKey: typeof settings?.apiKey === 'string' ? settings.apiKey.trim() : '',
    token: typeof settings?.token === 'string' ? settings.token.trim() : '',
    model:
      typeof settings?.model === 'string' && settings.model.trim().length > 0
        ? settings.model.trim()
        : DEFAULT_AI_ASK_SETTINGS.model,
    baseUrl: typeof settings?.baseUrl === 'string' ? normalizeBaseUrl(settings.baseUrl) : '',
  };
}

function toUserFacingAIAskError(error: unknown): Error {
  const code = readErrorField(error, 'code');
  const message = readErrorField(error, 'message');

  if (code === 'functions/failed-precondition' || /api key is not configured/i.test(message)) {
    return new Error('OPENAI_API_KEY missing');
  }

  if (code === 'functions/permission-denied' || /invalid openai api key/i.test(message)) {
    return new Error('Check your token or API key');
  }

  if (code === 'functions/resource-exhausted') {
    return new Error('Quota or rate limit reached');
  }

  if (code === 'functions/invalid-argument') {
    if (/base url/i.test(message)) {
      return new Error('Check Base URL');
    }

    if (/too long/i.test(message)) {
      return new Error('Question is too long');
    }

    return new Error('Check the question');
  }

  if (code === 'functions/unavailable' || /endpoint is unreachable/i.test(message)) {
    return new Error('Check Base URL');
  }

  if (code === 'functions/internal' || code === 'functions/deadline-exceeded') {
    return new Error('Try again soon');
  }

  return new Error('AI connection failed');
}

export function getAIAskSettingsStorageKey(userId: string | null | undefined): string {
  return `crypto-missions:ai-ask:settings:${userId ?? 'guest'}`;
}

export function readAIAskSettings(userId: string | null | undefined): AIAskSettings {
  if (typeof window === 'undefined') {
    return DEFAULT_AI_ASK_SETTINGS;
  }

  try {
    const rawValue = localStorage.getItem(getAIAskSettingsStorageKey(userId));
    if (!rawValue) {
      return DEFAULT_AI_ASK_SETTINGS;
    }

    return normalizeSettings(JSON.parse(rawValue));
  } catch (error) {
    console.error('Failed to read AI Ask settings:', error);
    return DEFAULT_AI_ASK_SETTINGS;
  }
}

export function writeAIAskSettings(userId: string | null | undefined, settings: AIAskSettings): AIAskSettings {
  const normalized = normalizeSettings(settings);

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(getAIAskSettingsStorageKey(userId), JSON.stringify(normalized));
    } catch (error) {
      console.error('Failed to save AI Ask settings:', error);
    }
  }

  return normalized;
}

export async function askAI(
  prompt: string,
  history: AIAskMessage[],
  options?: AskAIOptions
): Promise<AskAIResult> {
  const trimmedPrompt = prompt.trim();
  if (!trimmedPrompt) {
    throw new Error('Enter a question');
  }

  const settings = normalizeSettings(options);
  if (settings.usePersonalKey && !settings.baseUrl && !settings.apiKey && !settings.token) {
    throw new Error('Enter Base URL, token, or API key');
  }

  try {
    const payload: AskAIPayload = {
      prompt: trimmedPrompt,
      history: history.map(({ role, content }) => ({ role, content })),
    };

    if (settings.usePersonalKey) {
      if (settings.apiKey) {
        payload.apiKey = settings.apiKey;
      }
      if (settings.token) {
        payload.token = settings.token;
      }
      payload.model = settings.model;
      if (settings.baseUrl) {
        payload.baseUrl = settings.baseUrl;
      }
    }

    const response = await askAICallable(payload);
    return response.data;
  } catch (error) {
    throw toUserFacingAIAskError(error);
  }
}
